// Error reporting for @capacitor/google-maps instances. Map failures mostly
// don't throw anywhere React can see (async SDK calls, Google's own auth
// overlay), so without this they surface to users as gray/blank maps and
// never reach Logs Explorer. Everything here logs at ERROR level on purpose:
// prod drops WARN (see .env.prod VITE_LOG_LEVELS).

import { Capacitor } from '@capacitor/core';
import { logError } from '../services/loggerService';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

/**
 * Why a map couldn't be shown. Drives the fallback copy and whether a retry
 * is offered:
 * - `offline`: device reports no connection (overrides timeout/error/data)
 * - `timeout`: map never finished creating
 * - `error`: map creation threw, even after the automatic retry
 * - `config`: API key missing or rejected by Google; retrying can't help
 * - `render`: React render crash inside the map subtree
 * - `data`: the listings query for the map failed
 */
export type MapFailureReason = 'offline' | 'timeout' | 'error' | 'config' | 'render' | 'data';

export type MapStatus =
  | { state: 'loading' }
  | { state: 'ready' }
  | { state: 'failed'; reason: MapFailureReason };

/** Folds "the device is offline" into a failure reason, since that's the more useful thing to tell the user. */
export function resolveFailureReason(reason: MapFailureReason): MapFailureReason {
  if (reason === 'config') return reason;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  return reason;
}

// Ambient context that helps tell "our bug" from "user's environment" when
// reading a map failure in Logs Explorer.
function environmentContext() {
  return {
    platform: Capacitor.getPlatform(),
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    visibility: typeof document !== 'undefined' ? document.visibilityState : undefined,
  };
}

/**
 * Logs a map failure with a consistent message shape (`<mapName>: <stage> failed`)
 * so all map errors can be filtered together in Logs Explorer.
 */
export function logMapError(
  mapName: string,
  stage: string,
  error: unknown,
  data?: Record<string, unknown>,
) {
  const err = error instanceof Error ? error : new Error(String(error));
  logError(err, `${mapName}: ${stage} failed`, undefined, {
    mapName,
    stage,
    ...environmentContext(),
    ...data,
  });
}

let authHookInstalled = false;
// Sticky for the session: once Google rejects the key, every later map
// would fail the same way, so new map mounts can fail fast instead of
// waiting out their watchdog.
let authFailed = false;
const authFailureListeners = new Set<() => void>();

export function hasMapAuthFailed(): boolean {
  return authFailed;
}

/** Subscribe to Google Maps auth failures. Returns an unsubscribe function. */
export function subscribeMapAuthFailure(listener: () => void): () => void {
  authFailureListeners.add(listener);
  return () => {
    authFailureListeners.delete(listener);
  };
}

/**
 * Installs Google Maps JS API's global `gm_authFailure` callback. Google calls
 * it when the API key is rejected (bad key, referrer restriction, billing,
 * disabled API) and then paints its own "can't load Google Maps correctly"
 * overlay; nothing else tells us it happened. Web-only in practice: native
 * Capacitor maps use the platform SDKs, which never call it.
 */
export function initMapAuthFailureHook() {
  if (authHookInstalled || typeof window === 'undefined') return;
  authHookInstalled = true;

  const previous = window.gm_authFailure;
  window.gm_authFailure = () => {
    logMapError('GoogleMaps', 'auth', new Error('gm_authFailure'), {
      origin: window.location.origin,
      path: window.location.pathname,
    });
    authFailed = true;
    authFailureListeners.forEach((listener) => listener());
    previous?.();
  };
}
